import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ANALYTICS_SOURCE,
	ANALYTICS_EVENT_TYPE,
	ANALYTICS_TYPE,
	ANALYTICS_UI_ACTION,
	ANALYTICS_UI_SUBJECT,
	ANALYTICS_OPERATIONAL_ACTION,
	type AnalyticsOrigin,
} from '../../constants/snippetAnalytics.ts';

type SnippetUIEventData = {
	actionSubject: string;
	action: string;
	actionSubjectId: string;
	attributes?: Record<string, unknown>;
};

type SnippetOperationalEventData = {
	actionSubject: string;
	action: string;
	attributes?: Record<string, unknown>;
};

/**
 * Hook that provides pre-configured analytics helpers for the snippets package.
 *
 * Encapsulates `useAnalyticsEvents` and injects the shared source, event type,
 * and channel so callers only need to provide the event-specific fields.
 *
 * Shorthand helpers (fireButtonClickedAnalytic, fireModalClosedAnalytic, etc.)
 * cover the most common actionSubject + action combinations so callers only
 * need to supply the actionSubjectId and optional attributes.
 *
 * @param origin - Optional origin that is automatically included in every
 *   event's attributes. Per-call attributes with the same key take precedence.
 */
export const useSnippetAnalytics = (origin?: AnalyticsOrigin) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/** Merges the hook-level origin into attributes (per-call attributes take precedence). */
	const mergeAttributes = (
		attributes?: Record<string, unknown>,
	): Record<string, unknown> | undefined => {
		if (!origin) {
			return attributes;
		}
		return { origin, ...attributes };
	};

	const fireUIAnalytic = (data: SnippetUIEventData) => {
		createAnalyticsEvent({
			type: ANALYTICS_EVENT_TYPE.SEND_UI_EVENT,
			data: {
				source: ANALYTICS_SOURCE,
				type: ANALYTICS_TYPE.UI,
				...data,
				attributes: mergeAttributes(data.attributes),
			},
		}).fire();
	};

	const fireOperationalAnalytic = (data: SnippetOperationalEventData) => {
		createAnalyticsEvent({
			type: ANALYTICS_EVENT_TYPE.SEND_OPERATIONAL_EVENT,
			data: {
				source: ANALYTICS_SOURCE,
				type: ANALYTICS_TYPE.OPERATIONAL,
				...data,
				attributes: mergeAttributes(data.attributes),
			},
		}).fire();
	};

	// ─── Shorthand helpers ──────────────────────────────────────────────

	const fireButtonClickedAnalytic = (
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => {
		fireUIAnalytic({
			actionSubject: ANALYTICS_UI_SUBJECT.BUTTON,
			action: ANALYTICS_UI_ACTION.CLICKED,
			actionSubjectId,
			attributes,
		});
	};

	const fireModalClosedAnalytic = (
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => {
		fireUIAnalytic({
			actionSubject: ANALYTICS_UI_SUBJECT.MODAL,
			action: ANALYTICS_UI_ACTION.CLOSED,
			actionSubjectId,
			attributes,
		});
	};

	const fireTextFieldTypedAnalytic = (
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => {
		fireUIAnalytic({
			actionSubject: ANALYTICS_UI_SUBJECT.TEXT_FIELD,
			action: ANALYTICS_UI_ACTION.TYPED,
			actionSubjectId,
			attributes,
		});
	};

	const fireDropdownItemClickedAnalytic = (
		actionSubjectId: string,
		attributes?: Record<string, unknown>,
	) => {
		fireUIAnalytic({
			actionSubject: ANALYTICS_UI_SUBJECT.DROPDOWN_ITEM,
			action: ANALYTICS_UI_ACTION.CLICKED,
			actionSubjectId,
			attributes,
		});
	};

	const fireFailedAnalytic = (actionSubject: string, attributes?: Record<string, unknown>) => {
		fireOperationalAnalytic({
			actionSubject,
			action: ANALYTICS_OPERATIONAL_ACTION.FAILED,
			attributes,
		});
	};

	const fireSuccessAnalytic = (actionSubject: string, attributes?: Record<string, unknown>) => {
		fireOperationalAnalytic({
			actionSubject,
			action: ANALYTICS_OPERATIONAL_ACTION.SUCCEEDED,
			attributes,
		});
	};

	return {
		fireOperationalAnalytic,
		fireButtonClickedAnalytic,
		fireModalClosedAnalytic,
		fireTextFieldTypedAnalytic,
		fireDropdownItemClickedAnalytic,
		fireFailedAnalytic,
		fireSuccessAnalytic,
	};
};
